import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import { signOut } from '../context/jwt';
import { useAuthContext } from '../hooks';
import { canAccessBackend } from '../utils';

// ----------------------------------------------------------------------

/**
 * 管理员守卫
 *
 * 进入后台路由前要求具备 B 端访问能力（canAccessBackend）：
 *   - 平台超管（isSuperAdmin=true）
 *   - 租户管理员（isAdmin=true）
 *   - 受邀后台员工（isStaff=true）
 *
 * 纯 C 端用户 → 立即清 token + 跳回登录页
 *
 * 必须套在 AuthGuard 之内（依赖 authenticated 状态）。
 */
type AdminGuardProps = {
  children: React.ReactNode;
};

export function AdminGuard({ children }: AdminGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, loading, checkUserSession } = useAuthContext();

  const [isChecking, setIsChecking] = useState(true);

  const checkPermissions = useCallback(async (): Promise<void> => {
    if (loading) return;

    if (!canAccessBackend(user)) {
      // 清掉本地 token 并同步 AuthContext，避免 GuestGuard 仍认为已登录而反复跳转
      await signOut();
      await checkUserSession?.();
      const queryString = new URLSearchParams({
        returnTo: pathname,
        reason: 'not_admin',
      }).toString();
      router.replace(`${paths.auth.jwt.signIn}?${queryString}`);
      return;
    }

    setIsChecking(false);
  }, [loading, user, pathname, router, checkUserSession]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  if (isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
